import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import {
  Button,
  Select,
  DatePicker,
  InputNumber,
  Form,
  Alert,
  Card,
  Space,
  message,
} from "antd";
import axios from "axios";
import toast from "react-hot-toast";
import Header from "../../../../Componets/Header";
import headers from "../../../../Data/Forms/Accounting/BankReconcilliation/headers";
import { get_all_accounts } from "../../../../Controllers/Accounting/Services/chartOfAccountsService";
import {
  download_csv_template,
  upload_bank_statement,
} from "../../../../Controllers/Accounting/BankReconcilliation";
import ScrollWrapper from "../../../../Componets/ScrollWrapper";

const BankStatementUpload = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [header, setHeader] = useState(headers);
  const [form] = Form.useForm();

  React.useEffect(() => {
    fetchBankAccounts();
  }, []);

  const fetchBankAccounts = async () => {
    try {
      const token = localStorage.getItem("user");
      const data = { type: "Bank", category: "Assets" };
      const response = await get_all_accounts(data);
      if (response.data) {
        setBankAccounts(response.data);
      }
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "text/csv") {
      setFile(selectedFile);
    } else {
      toast.error("Please select a valid CSV file");
      setFile(null);
    }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("user");
      const response = await download_csv_template();

      console.log("ajfiklsa", response);

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "bank_statement_template.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Template downloaded successfully");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleSubmit = async (values) => {
    if (!file) {
      toast.error("Please select a CSV file");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("bankAccountId", values.bankAccountId);
    formData.append("statementDate", values.statementDate.format("YYYY-MM-DD"));
    formData.append(
      "statementPeriodStart",
      values.statementPeriodStart.format("YYYY-MM-DD"),
    );
    formData.append(
      "statementPeriodEnd",
      values.statementPeriodEnd.format("YYYY-MM-DD"),
    );
    formData.append("openingBalance", values.openingBalance || 0);
    formData.append("closingBalance", values.closingBalance);

    try {
      const token = localStorage.getItem("user");
      const response = await upload_bank_statement(formData);

      if (response?.success) {
        setUploadResult(response.data);
        toast.success(response.message);
        setTimeout(() => {
          navigate(
            `/app/bank-reconciliation/statement/${response.data._id}`,
          );
        }, 2000);
      } else {
        toast.error(response?.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(
        error.response?.message || "Failed to upload bank statement",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Header headers={header} setHeaders={setHeader}>
      <ScrollWrapper>
      <Card title="Upload Bank Statement" className="shadow-md">
        <div className="mb-6">
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
            Download CSV Template
          </Button>
          <span className="ml-4 text-gray-500 text-sm">
            Download the template to see the required format
          </span>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="bankAccountId"
            label="Bank Account"
            rules={[
              { required: true, message: "Please select a bank account" },
            ]}
          >
            <Select placeholder="Select bank account" showSearch>
              {bankAccounts.map((account) => (
                <Select.Option key={account._id} value={account._id}>
                  {account.name} ({account.code})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="statementDate"
            label="Statement Date"
            rules={[
              { required: true, message: "Please select statement date" },
            ]}
          >
            <DatePicker className="w-full" />
          </Form.Item>

          <Form.Item
            name="statementPeriodStart"
            label="Period Start Date"
            rules={[
              { required: true, message: "Please select period start date" },
            ]}
          >
            <DatePicker className="w-full" />
          </Form.Item>

          <Form.Item
            name="statementPeriodEnd"
            label="Period End Date"
            rules={[
              { required: true, message: "Please select period end date" },
            ]}
          >
            <DatePicker className="w-full" />
          </Form.Item>

          <Form.Item name="openingBalance" label="Opening Balance">
            <InputNumber
              className="w-full"
              precision={3}
              placeholder="0.000"
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
            />
          </Form.Item>

          <Form.Item
            name="closingBalance"
            label="Closing Balance"
            rules={[
              { required: true, message: "Please enter closing balance" },
            ]}
          >
            <InputNumber
              className="w-full"
              precision={3}
              placeholder="0.000"
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
            />
          </Form.Item>

          <Form.Item label="CSV File" required>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="border rounded p-2 w-full"
            />
            {file && (
              <div className="mt-2 text-green-600">Selected: {file.name}</div>
            )}
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Upload Statement
              </Button>
              <Button onClick={() => navigate(-1)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>

        {uploadResult && (
          <Alert
            message="Upload Successful"
            description={
              <div>
                <p>Total Transactions: {uploadResult.totalTransactions}</p>
                <p>Total Debits: {uploadResult.totalDebits}</p>
                <p>Total Credits: {uploadResult.totalCredits}</p>
                <p>Opening Balance: {uploadResult.openingBalance}</p>
                <p>Closing Balance: {uploadResult.closingBalance}</p>
                {uploadResult.warnings && (
                  <div className="mt-2">
                    <strong>Warnings:</strong>
                    <ul className="list-disc pl-5">
                      {uploadResult.warnings.map((warning, idx) => (
                        <li key={idx} className="text-yellow-600">
                          {warning}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            }
            type="success"
            showIcon
            className="mt-4"
          />
        )}
      </Card>
      </ScrollWrapper>
    </Header>
  );
};

export default BankStatementUpload;
